import { getMissionsForUser } from '@/__fixtures__/missions.mock';
import { ActivityOption, ActivityType } from '@/shared/types/timesheet';
import { getMissionActivityValue, getMissionLabel } from '@/shared/utils/mission';

/** Catalogue global (valable pour tous les utilisateurs) */
export const globalActivityOptions: ActivityOption[] = [
  {
    id: 'ga-1',
    type: ActivityType.OTHER,
    value: 'jour-non-facture',
    label: 'Jour non facturé',
    group: 'Autres',
    allowRemoteFlag: false,
    requiresJustification: false,
    justificationHint: null,
  },
  {
    id: 'ga-2',
    type: ActivityType.ABSENCE_JUSTIFIED,
    value: 'absence-enfant-malade',
    label: 'Absence - Enfant Malade',
    group: 'Absences',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Certificat médical à fournir sous 48h.',
  },
  {
    id: 'ga-3',
    type: ActivityType.ABSENCE_TO_BE_JUSTIFIED,
    value: 'absence-conge-paternite',
    label: 'Absence - Congé Paternité',
    group: 'Absences',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Acte de naissance obligatoire.',
  },
  {
    id: 'ga-4',
    type: ActivityType.ABSENCE_TO_BE_JUSTIFIED,
    value: 'absence-conge-maternite',
    label: 'Absence - Congé Maternité',
    group: 'Absences',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Déclaration médicale obligatoire.',
  },
  {
    id: 'ga-5',
    type: ActivityType.ABSENCE_TO_BE_JUSTIFIED,
    value: 'absence-naissance-3j',
    label: 'Absence - 3 jours pour naissance',
    group: 'Absences',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Acte de naissance obligatoire.',
  },
  {
    id: 'ga-6',
    type: ActivityType.ABSENCE_TO_BE_JUSTIFIED,
    value: 'absence-maladie',
    label: 'Absence - Maladie',
    group: 'Absences',
    allowRemoteFlag: false,
    requiresJustification: true,
    justificationHint: 'Certificat médical à fournir sous 48h.',
  },
];

/** Fallback projet si le consultant n'a aucune mission associée (intercontrat). */
const defaultMissionOption: ActivityOption = {
  id: 'ma-generic',
  type: ActivityType.PROJECT,
  value: 'intercontrat',
  label: 'Intercontrat',
  group: 'Projets',
  allowRemoteFlag: true,
  requiresJustification: false,
  justificationHint: null,
};

/**
 * Options d'activité disponibles pour un consultant.
 *
 * Les options « Projet » sont dérivées de ses missions (celles utilisant le CRA), afin que
 * chaque ligne projet du CRA soit rattachée à une mission réelle via `missionId`.
 * Les absences / jours non facturés proviennent du catalogue global.
 * Ordre: Projets > Absences > Autres.
 */
export function getActivityOptionsForUser(userId: string): ActivityOption[] {
  const missionOptions: ActivityOption[] = getMissionsForUser(userId)
    .filter(m => m.usesTimesheet !== false)
    .map(m => ({
      id: `ma-${m.id}`,
      type: ActivityType.PROJECT,
      value: getMissionActivityValue(m),
      label: getMissionLabel(m),
      group: 'Projets',
      missionId: m.id,
      allowRemoteFlag: true,
      requiresJustification: false,
      justificationHint: null,
    }));

  const projects = missionOptions.length > 0 ? missionOptions : [defaultMissionOption];
  return [...projects, ...globalActivityOptions];
}
